import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { voiceApi } from '../api/voice.api.js';

function friendlyVoiceError(error) {
  const code = error?.code ? `Twilio ${error.code}` : '';
  const raw = error?.message || error?.description || 'Voice device error';
  if (String(error?.code) === '31005') {
    return `${code}: Connection failed. Open Calls and tap Retry. If it continues, check the customer's TwiML App URL, microphone permission, and mobile network.`;
  }
  return [code, raw].filter(Boolean).join(': ');
}

async function requestMicrophoneAccess() {
  if (!navigator.mediaDevices?.getUserMedia) return;
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => track.stop());
}

export function useTwilioDevice(enabled) {
  const deviceRef = useRef(null);
  const activeRef = useRef(null);
  const registeringRef = useRef(null);
  const retryRef = useRef(null);
  const [status, setStatus] = useState('Disconnected');
  const [incoming, setIncoming] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [identity, setIdentity] = useState('');
  const [lastError, setLastError] = useState('');

  const register = async (options = {}) => {
    if (!enabled) return;
    if (registeringRef.current) return registeringRef.current;

    registeringRef.current = (async () => {
      try {
        clearTimeout(retryRef.current);
        setStatus('Connecting');
        setLastError('');
        if (options.requestAudio) await requestMicrophoneAccess();
        const { Device } = await import('@twilio/voice-sdk');
        if (!Device.isSupported) throw new Error('This browser does not support Twilio Voice calls');
        const { data } = await voiceApi.token();
        setIdentity(data.identity);

        if (deviceRef.current) {
          deviceRef.current.updateToken(data.token);
          if (deviceRef.current.state === 'unregistered') await deviceRef.current.register();
          setStatus(deviceRef.current.state === 'registered' ? 'Connected' : 'Connecting');
          return true;
        }

        const device = new Device(data.token, {
          logLevel: 1,
          codecPreferences: ['opus', 'pcmu'],
          enableImprovedSignalingErrorPrecision: true,
        });
        deviceRef.current = device;

        device.on('registering', () => setStatus('Connecting'));
        device.on('registered', () => setStatus('Connected'));
        device.on('unregistered', () => setStatus('Disconnected'));
        device.on('tokenWillExpire', async () => {
          const fresh = await voiceApi.token();
          device.updateToken(fresh.data.token);
        });
        device.on('incoming', (call) => {
          call.on('cancel', () => setIncoming(null));
          call.on('reject', () => setIncoming(null));
          call.on('disconnect', () => {
            activeRef.current = null;
            setActiveCall(null);
            setIncoming(null);
          });
          call.on('accept', () => {
            activeRef.current = call;
            setActiveCall(call);
            setIncoming(null);
          });
          setIncoming(call);
          toast(`Incoming call from ${call.parameters?.From || 'unknown caller'}`);
        });
        device.on('disconnect', () => {
          activeRef.current = null;
          setActiveCall(null);
          setOutgoing(null);
        });
        device.on('error', (error) => {
          const message = friendlyVoiceError(error);
          setLastError(message);
          setStatus('Disconnected');
          clearTimeout(retryRef.current);
          retryRef.current = setTimeout(() => register(), 3500);
        });

        await device.register();
        return true;
      } catch (err) {
        const message = err.response?.data?.message || err.message || 'Could not connect voice device';
        setLastError(message);
        setStatus('Disconnected');
        return false;
      } finally {
        registeringRef.current = null;
      }
    })();

    return registeringRef.current;
  };

  useEffect(() => {
    if (!enabled) return undefined;
    register();
    const timer = setInterval(register, 50 * 60 * 1000);
    return () => {
      clearInterval(timer);
      clearTimeout(retryRef.current);
      deviceRef.current?.destroy?.();
      deviceRef.current = null;
      registeringRef.current = null;
    };
  }, [enabled]);

  const makeCall = async (to) => {
    const destination = String(to || '').trim();
    if (!destination) throw new Error('Enter a phone number');
    if (!deviceRef.current || status !== 'Connected') {
      const registered = await register({ requestAudio: true });
      if (!registered || !deviceRef.current || deviceRef.current.state !== 'registered') {
        throw new Error('Voice device is not connected. Allow microphone permission and tap Retry.');
      }
    }
    const call = await deviceRef.current.connect({ params: { To: destination } });
    setOutgoing(call);
    call.on('accept', () => {
      activeRef.current = call;
      setActiveCall(call);
      setOutgoing(null);
    });
    const clear = () => {
      if (activeRef.current === call) activeRef.current = null;
      setOutgoing(null);
      setActiveCall((current) => (current === call ? null : current));
    };
    call.on('cancel', clear);
    call.on('disconnect', clear);
    call.on('reject', clear);
    call.on('error', clear);
    return call;
  };

  return {
    status,
    identity,
    lastError,
    incoming,
    outgoing,
    activeCall,
    accept: async () => {
      try { await requestMicrophoneAccess(); } catch {}
      incoming?.accept();
    },
    reject: () => { incoming?.reject(); setIncoming(null); },
    makeCall,
    cancelOutgoing: () => { outgoing?.disconnect(); setOutgoing(null); },
    hangup: () => { activeRef.current?.disconnect(); setActiveCall(null); },
    mute: (muted) => activeRef.current?.mute(muted),
    register,
  };
}
